import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, ChevronDown, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import StoryUnderstandingPanel from '../components/StoryUnderstandingPanel';
import RegenerateConfirmModal from '../components/RegenerateConfirmModal';
import BeatCardV2 from '../components/BeatCardV2';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const USE_BEATS_V2 = (process.env.REACT_APP_USE_BEATS_V2 ?? 'true') !== 'false';

// ── FIPR stage configuration ───────────────────────────────────────────────
const STAGES = [
  {
    id: 'FUNNEL',
    label: 'Funnel',
    subtitle: 'Setup & World Building',
    color: '#60a5fa',
    glow: 'rgba(96,165,250,0.15)',
    border: 'rgba(96,165,250,0.25)',
    bg: 'rgba(96,165,250,0.06)',
    letter: 'F',
    desc: 'Establishes world, characters & status quo',
  },
  {
    id: 'INCITING',
    label: 'Inciting Incident',
    subtitle: 'The Trigger',
    color: '#F5A623',
    glow: 'rgba(245,166,35,0.2)',
    border: 'rgba(245,166,35,0.35)',
    bg: 'rgba(245,166,35,0.07)',
    letter: 'I',
    desc: 'The event that breaks the normal world',
  },
  {
    id: 'COMPLICATIONS',
    label: 'Progressive Complications',
    subtitle: 'Rising Tension',
    color: '#fb7185',
    glow: 'rgba(251,113,133,0.15)',
    border: 'rgba(251,113,133,0.25)',
    bg: 'rgba(251,113,133,0.06)',
    letter: 'P',
    desc: 'Obstacles, turning points & dark moments',
  },
  {
    id: 'RESOLUTION',
    label: 'Resolution',
    subtitle: 'Climax & Aftermath',
    color: '#4ade80',
    glow: 'rgba(74,222,128,0.15)',
    border: 'rgba(74,222,128,0.25)',
    bg: 'rgba(74,222,128,0.06)',
    letter: 'R',
    desc: 'Climax, transformation & thematic payoff',
  },
];

// ── Beat Card ──────────────────────────────────────────────────────────────
function BeatCard({ beat, stageColor, stageBg, stageBorder }) {
  const [expanded, setExpanded] = useState(false);
  const [from, to] = beat.emotional_shift?.includes('→')
    ? beat.emotional_shift.split('→').map(s => s.trim())
    : [beat.emotional_shift, ''];

  return (
    <div
      data-testid={`beat-card-${beat.id}`}
      className="rounded-xl border p-4 cursor-pointer transition-all duration-200 group"
      style={{
        background: expanded ? stageBg : 'var(--cb-surface)',
        borderColor: expanded ? stageBorder : 'var(--cb-border)',
        boxShadow: expanded ? `0 0 20px ${stageColor}15` : 'none',
      }}
      onClick={() => setExpanded(e => !e)}
      onMouseEnter={e => { if (!expanded) e.currentTarget.style.borderColor = stageBorder; }}
      onMouseLeave={e => { if (!expanded) e.currentTarget.style.borderColor = 'var(--cb-border)'; }}
    >
      {/* Beat type + scene number */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-[11px] font-bold uppercase tracking-wider leading-tight"
              style={{ color: stageColor }}>
          {beat.beat_type}
        </span>
        <span className="text-[10px] shrink-0 rounded px-1.5 py-0.5 font-mono"
              style={{ background: 'var(--cb-surface-h)', color: 'var(--cb-text-dim)' }}>
          {formatSceneNumber(beat.scene_number, { prefix: '#' })}
        </span>
      </div>

      {/* Scene header */}
      <p className="text-[11px] mb-2 font-medium truncate" style={{ color: 'var(--cb-text-sec)' }}>
        {beat.scene_header}
      </p>

      {/* Summary */}
      <p className="text-[13px] leading-snug mb-3" style={{ color: 'var(--cb-text)' }}>
        {beat.summary}
      </p>

      {/* Emotional shift */}
      {beat.emotional_shift && (
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                style={{ background: `${stageColor}15`, color: stageColor }}>
            {from}
          </span>
          <ArrowRight className="h-3 w-3" style={{ color: 'var(--cb-text-dim)' }} />
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--cb-text-sec)' }}>
            {to}
          </span>
        </div>
      )}

      {/* Page position */}
      <div className="flex items-center justify-between">
        <span className="text-[10px]" style={{ color: 'var(--cb-text-dim)' }}>
          {beat.page_position}
        </span>
        <ChevronDown
          className="h-3.5 w-3.5 transition-transform duration-200"
          style={{ color: 'var(--cb-text-dim)', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          strokeWidth={2}
        />
      </div>

      {/* Expanded: significance */}
      {expanded && beat.significance && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--cb-border)' }}>
          <p className="text-[11px] italic leading-relaxed" style={{ color: 'var(--cb-text-sec)' }}>
            {beat.significance}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Stage Column ───────────────────────────────────────────────────────────
function StageColumn({ stage, beats, schemaVersion, projectId, onStageRegen,
                       stageStatus, stageError }) {
  // M3.14: Stage health dot — green=ready, amber=failed (with tooltip), gray=none
  const healthColor = stageStatus === 'ready' ? '#4ade80'
                    : stageStatus === 'failed' ? '#fb7185'
                    : 'rgba(255,255,255,0.18)';
  const healthTitle = stageStatus === 'ready' ? 'Last run: ready'
                    : stageStatus === 'failed' ? (`Last run failed: ${stageError || 'unknown error'}`)
                    : 'Not yet generated';
  const renderBeat = (beat) => {
    if (schemaVersion === 2) {
      return (
        <BeatCardV2
          key={beat.id}
          beat={beat}
          stageColor={stage.color}
          stageBg={stage.bg}
          stageBorder={stage.border}
          projectId={projectId}
        />
      );
    }
    return (
      <BeatCard
        key={beat.id}
        beat={beat}
        stageColor={stage.color}
        stageBg={stage.bg}
        stageBorder={stage.border}
      />
    );
  };

  return (
    <div className="flex flex-col min-w-[240px] flex-1" data-testid={`stage-col-${stage.id}`}>
      {/* Stage header */}
      <div className="mb-4 rounded-xl p-4 border"
           style={{ background: stage.bg, borderColor: stage.border, boxShadow: `0 0 30px ${stage.glow}` }}>
        <div className="flex items-center gap-2.5 mb-1">
          <div className="h-7 w-7 rounded-lg flex items-center justify-center font-black text-sm shrink-0 text-black"
               style={{ background: stage.color }}>
            {stage.letter}
          </div>
          <div className="min-w-0">
            <div className="font-bold text-sm leading-tight" style={{ color: stage.color }}>
              {stage.label}
            </div>
            <div className="text-[10px]" style={{ color: 'var(--cb-text-dim)' }}>
              {stage.subtitle}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {/* M3.14 Stage Health dot */}
            <span
              data-testid={`stage-health-${stage.id}`}
              title={healthTitle}
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: healthColor,
                       boxShadow: stageStatus === 'ready' ? '0 0 8px #4ade8090' :
                                  stageStatus === 'failed' ? '0 0 8px #fb718590' : 'none' }}
            />
            <div className="text-[11px] font-bold rounded-full px-2 py-0.5"
                 style={{ background: `${stage.color}20`, color: stage.color }}>
              {beats.length}
            </div>
            {schemaVersion === 2 && onStageRegen && (
              <button
                type="button"
                onClick={() => onStageRegen(stage.id)}
                data-testid={`stage-regen-${stage.id}-btn`}
                title={`Regenerate ${stage.label}`}
                className="h-6 w-6 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                style={{ background: 'var(--cb-surface-h)', color: stage.color }}
              >
                <RefreshCw className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
        <p className="text-[10px]" style={{ color: 'var(--cb-text-dim)' }}>{stage.desc}</p>
      </div>

      {/* Beat cards */}
      <div className="space-y-3 flex-1">
        {beats.length === 0 ? (
          <div className="rounded-xl border border-dashed p-6 text-center"
               style={{ borderColor: 'var(--cb-border)' }}>
            <p className="text-xs" style={{ color: 'var(--cb-text-dim)' }}>No beats identified</p>
          </div>
        ) : (
          beats
            .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
            .map(renderBeat)
        )}
      </div>
    </div>
  );
}

// ── Skeleton loading ───────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {STAGES.map(stage => (
        <div key={stage.id} className="min-w-[240px] flex-1">
          <div className="h-24 rounded-xl mb-4 animate-pulse" style={{ background: 'var(--cb-surface)' }} />
          {[1, 2, 3].map(i => (
            <div key={i} className="h-28 rounded-xl mb-3 animate-pulse" style={{ background: 'var(--cb-surface)' }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function BeatBoardPage() {
  const { projectId } = useParams();
  const { token }     = useAuth();
  const headers       = { Authorization: `Bearer ${token}` };

  const [board,      setBoard]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/beat-board`, { headers });
      setBoard(data);
      if (data.status === 'generating') {
        // poll while generating
        setTimeout(() => load(), 3000);
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while generating
  useEffect(() => {
    if (board?.status !== 'generating') return;
    const timer = setTimeout(() => load(), 3000);
    return () => clearTimeout(timer);
  }, [board]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (force = false) => {
    setGenerating(true);
    try {
      await axios.post(`${API}/projects/${projectId}/beat-board/generate`, {}, { headers });
      toast.success('FIPR analysis started — takes ~30 seconds');
      setBoard({ status: 'generating', beats: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start analysis');
    } finally {
      setGenerating(false);
    }
  };

  // Group beats by stage — v2 if available, else legacy v1
  const isV2 = USE_BEATS_V2 && (board?.schema_version === 2 || board?.beats_v2);
  const v2Stages = board?.beats_v2 || {};
  const beatsByStage = {};
  STAGES.forEach(s => { beatsByStage[s.id] = []; });
  if (isV2) {
    const stageMap = {
      FUNNEL: v2Stages.funnel || [],
      INCITING: v2Stages.inciting || [],
      COMPLICATIONS: v2Stages.complications || [],
      RESOLUTION: v2Stages.resolution || [],
    };
    Object.entries(stageMap).forEach(([k, arr]) => { beatsByStage[k] = arr; });
  } else {
    (board?.beats || []).forEach(b => {
      if (beatsByStage[b.stage]) beatsByStage[b.stage].push(b);
    });
  }

  const totalBeats   = isV2
    ? Object.values(beatsByStage).reduce((a, v) => a + v.length, 0)
    : (board?.beats || []).length;
  const isReady      = isV2 ? Object.values(beatsByStage).some(v => v.length > 0) : board?.status === 'ready';
  const isGenerating = board?.status === 'generating' || generating;
  const hasError     = board?.status === 'error';

  // Resolve script version label for the banner. We get script_id from the
  // board, then look it up in /script-status to display the version_number.
  const [versionLabel, setVersionLabel] = useState(null);
  useEffect(() => {
    const sid = board?.script_id;
    if (!sid) { setVersionLabel(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/projects/${projectId}/script-status`, { headers });
        const v = data?.version_number ?? null;
        if (!cancelled) setVersionLabel(v != null ? `v${v}` : sid.slice(0, 8));
      } catch {
        if (!cancelled) setVersionLabel(sid.slice(0, 8));
      }
    })();
    return () => { cancelled = true; };
  }, [board?.script_id, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasNewerActiveScript =
    board?.active_script_id && board?.script_id && board.active_script_id !== board.script_id;

  // ── Phase 2c: regenerate flow with confirmation + cascade ────────────────
  const [regenModal, setRegenModal] = useState(null);    // {scope, stage} | null
  const [cascadeModal, setCascadeModal] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);

  const fireRegenerate = async (stage = null) => {
    setRegenBusy(true);
    try {
      const url = stage
        ? `${API}/projects/${projectId}/beat-board/regenerate-stage`
        : `${API}/projects/${projectId}/beat-board/regenerate-current`;
      const body = stage ? { stage } : {};
      const { data } = await axios.post(url, body, { headers });
      if (data?.status === 'story_understanding_required') {
        setRegenModal(null);
        setCascadeModal(true);
        return;
      }
      if (data?.status === 'in_flight') {
        toast.info('Another regenerate is already in flight.');
      } else if (data?.status === 'ready') {
        const cost = data?.actual_cost_usd;
        const costSuffix = (cost != null && cost > 0) ? ` · $${cost.toFixed(4)} (FiPr AI)` : '';
        toast.success((stage ? `${stage} regenerated.` : 'Beat board regenerated.') + costSuffix);
        await load();
      } else {
        toast.success('Triggered.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Regenerate failed.');
    } finally {
      setRegenBusy(false);
      setRegenModal(null);
    }
  };

  const runCascade = async () => {
    setRegenBusy(true);
    try {
      // Stage-1 generate first
      const sid = board?.script_id;
      const { data: su } = await axios.post(
        `${API}/scripts/${sid}/story-understanding/generate`,
        { force_regenerate: false },
        { headers },
      );
      if (su?.status === 'budget_blocked') {
        toast.error('Daily budget reached — try again later.');
        return;
      }
      // Then full regenerate
      const { data } = await axios.post(
        `${API}/projects/${projectId}/beat-board/regenerate-current`, {},
        { headers },
      );
      if (data?.status === 'ready') {
        toast.success('Story understanding generated + beat board regenerated.');
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cascade failed.');
    } finally {
      setRegenBusy(false);
      setCascadeModal(false);
    }
  };

  const regenerateForCurrent = () => setRegenModal({ scope: 'full' });

  return (
    <div className="flex flex-col h-full min-h-screen" style={{ background: 'var(--cb-bg)' }}>

      {/* ── Header ── */}
      <div className="px-6 pt-6 pb-4 border-b flex items-start justify-between gap-4 flex-wrap"
           style={{ borderColor: 'var(--cb-border)' }}>
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="flex gap-1">
              {STAGES.map(s => (
                <div key={s.id} className="h-5 w-5 rounded flex items-center justify-center font-black text-[10px] text-black"
                     style={{ background: s.color }}>
                  {s.letter}
                </div>
              ))}
            </div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--cb-text)' }}>
              FIPR Beat Board
            </h1>
          </div>
          <p className="text-sm" style={{ color: 'var(--cb-text-sec)' }}>
            Funnel → Inciting Incident → Progressive Complications → Resolution
          </p>
          {/* Dispatch B Phase 1: script-version banner */}
          {versionLabel && (
            <div
              data-testid="beatboard-version-banner"
              className="mt-2 inline-flex items-center gap-2 text-xs font-mono px-2.5 py-1 rounded-md border"
              style={{
                background: 'rgba(245,166,35,0.08)',
                borderColor: 'rgba(245,166,35,0.25)',
                color: 'var(--cb-accent)',
              }}
            >
              <span className="uppercase tracking-wider font-semibold">Beat board for script</span>
              <span className="font-bold">{versionLabel}</span>
              {board?.scene_count != null && (
                <span style={{ color: 'var(--cb-text-sec)' }}>· {board.scene_count} scenes</span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {isReady && (
            <span className="text-sm" style={{ color: 'var(--cb-text-dim)' }}>
              {totalBeats} beats identified
            </span>
          )}
          <Button
            onClick={isReady ? () => setRegenModal({ scope: 'full' }) : () => generate()}
            disabled={isGenerating}
            className="font-semibold h-10 px-5"
            style={{ background: 'var(--cb-accent)', color: '#000' }}
            data-testid="generate-beat-board-btn"
          >
            {isGenerating ? (
              <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Analysing…</>
            ) : isReady ? (
              <><RefreshCw className="h-4 w-4 mr-2" /> Regenerate</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Generate Beat Board</>
            )}
          </Button>
        </div>
      </div>

      {/* Phase 2c — Story Understanding panel */}
      {board?.script_id && (
        <StoryUnderstandingPanel
          scriptId={board.script_id}
          storyUnderstanding={board.story_understanding}
          scriptUpdatedAt={board.script_updated_at}
          onRegenerated={load}
          token={token}
        />
      )}

      {/* Dispatch B Phase 1: regenerate-for-current-version prompt */}
      {hasNewerActiveScript && (
        <div
          data-testid="regenerate-prompt"
          className="mx-6 mt-4 flex items-center justify-between gap-3 rounded-lg border px-4 py-3"
          style={{
            background: 'rgba(96,165,250,0.06)',
            borderColor: 'rgba(96,165,250,0.25)',
          }}
        >
          <div className="text-sm" style={{ color: 'var(--cb-text)' }}>
            <span className="font-semibold" style={{ color: '#60a5fa' }}>Newer script version detected.</span>{' '}
            This beat board was generated against an older version. Regenerate for the current version?
          </div>
          <Button
            onClick={regenerateForCurrent}
            data-testid="regenerate-prompt-btn"
            className="font-semibold"
            style={{ background: '#60a5fa', color: '#000' }}
          >
            Regenerate for current version
          </Button>
        </div>
      )}

      {/* ── Body ── */}
      <div className="flex-1 overflow-auto p-6">

        {/* Loading */}
        {loading && <Skeleton />}

        {/* Generating skeleton + message */}
        {!loading && isGenerating && (
          <div>
            <div className="flex items-center gap-3 mb-6 p-4 rounded-xl border"
                 style={{ background: 'rgba(245,166,35,0.07)', borderColor: 'rgba(245,166,35,0.25)' }}>
              <RefreshCw className="h-4 w-4 animate-spin shrink-0" style={{ color: 'var(--cb-accent)' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--cb-accent)' }}>
                  FiPr AI is analysing your screenplay…
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--cb-text-dim)' }}>
                  Reading scene synopses · Identifying FIPR beats · Building the board                </p>
              </div>
            </div>
            <Skeleton />
          </div>
        )}

        {/* Error */}
        {!loading && hasError && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className="text-base font-semibold mb-2" style={{ color: 'var(--cb-text)' }}>Analysis Failed</p>
            <p className="text-sm mb-4" style={{ color: 'var(--cb-text-dim)' }}>
              {board?.error || 'Something went wrong. Make sure the breakdown is complete.'}
            </p>
            <Button onClick={() => generate()} style={{ background: 'var(--cb-accent)', color: '#000' }}>
              <Sparkles className="h-4 w-4 mr-2" /> Try Again
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !isGenerating && !hasError && !isReady && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="flex gap-2 mb-6">
              {STAGES.map(s => (
                <div key={s.id} className="h-12 w-12 rounded-xl flex items-center justify-center font-black text-xl text-black"
                     style={{ background: s.color, opacity: 0.7 }}>
                  {s.letter}
                </div>
              ))}
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--cb-text)' }}>
              Generate Your FIPR Beat Board
            </h2>
            <p className="text-sm mb-6 max-w-md" style={{ color: 'var(--cb-text-sec)' }}>
              FiPr AI will read your script's scene synopses and identify the key story beats
              across the Funnel, Inciting Incident, Progressive Complications, and Resolution stages.
            </p>
            <Button
              onClick={() => generate()}
              disabled={generating}
              className="font-semibold h-11 px-8 text-base"
              style={{ background: 'var(--cb-accent)', color: '#000' }}
              data-testid="generate-beat-board-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Generate Beat Board
            </Button>
            <p className="text-xs mt-3" style={{ color: 'var(--cb-text-dim)' }}>Takes ~30 seconds · Cached permanently</p>
          </div>
        )}

        {/* ── Beat board — 4 column lanes ── */}
        {isReady && (
          <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '60vh' }}>
            {STAGES.map(stage => (
              <StageColumn
                key={stage.id}
                stage={stage}
                beats={beatsByStage[stage.id] || []}
                schemaVersion={isV2 ? 2 : 1}
                projectId={projectId}
                onStageRegen={isV2 ? (s => setRegenModal({ scope: 'stage', stage: s })) : null}
                stageStatus={(v2Stages.stage_statuses || {})[stage.id] || null}
                stageError={(v2Stages.stage_errors || {})[stage.id] || null}
              />
            ))}
          </div>
        )}
      </div>

      {/* Phase 2c — Regenerate confirmation modal */}
      <RegenerateConfirmModal
        open={Boolean(regenModal)}
        onClose={() => !regenBusy && setRegenModal(null)}
        onConfirm={() => fireRegenerate(regenModal?.scope === 'stage' ? regenModal.stage : null)}
        scope={regenModal?.scope || 'full'}
        stageLabel={regenModal?.stage || ''}
        busy={regenBusy}
      />

      {/* Phase 2c — Cascade modal (story_understanding required) */}
      <RegenerateConfirmModal
        open={cascadeModal}
        onClose={() => !regenBusy && setCascadeModal(false)}
        onConfirm={runCascade}
        scope="full"
        cascade={true}
        busy={regenBusy}
      />
    </div>
  );
}
